import React, { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  IconButton,
  Paper,
  TableSortLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Issue } from './types';

interface IssuesListViewProps {
  issues: Issue[];
  onEditIssue: (issue: Issue) => void;
  onDeleteIssue: (issueId: string) => void;
  canEdit?: boolean;
  canDelete?: boolean;
}

const statusLabel: Record<string, string> = {
  new: 'Ny',
  in_progress: 'Pågår',
  ready_for_test: 'Redo för test',
  done: 'Klar',
};

const statusColor = (status: string): 'default' | 'primary' | 'warning' | 'success' | 'info' => {
  if (status === 'new') return 'primary';
  if (status === 'in_progress') return 'warning';
  if (status === 'ready_for_test') return 'info';
  if (status === 'done') return 'success';
  return 'default';
};

const typeLabel: Record<string, string> = {
  bug: 'Bugg',
  fix: 'Fix',
  feature: 'Funktion',
  improvement: 'Förbättring',
};

const typeColor = (type: string): 'error' | 'warning' | 'success' | 'info' | 'default' => {
  if (type === 'bug') return 'error';
  if (type === 'fix') return 'warning';
  if (type === 'feature') return 'success';
  if (type === 'improvement') return 'info';
  return 'default';
};

const statusOrder: Record<string, number> = {
  new: 1,
  in_progress: 2,
  ready_for_test: 3,
  done: 4,
};

const priorityColor = (priority: number): 'error' | 'warning' | 'info' | 'default' => {
  if (priority === 1) return 'error';
  if (priority === 2) return 'warning';
  if (priority === 3) return 'info';
  return 'default';
};

const priorityLabel = (priority: number): string => {
  if (priority === 1) return 'Kritisk';
  if (priority === 2) return 'Hög';
  if (priority === 3) return 'Normal';
  return 'Låg';
};

const formatDate = (value: string) => {
  try {
    return new Date(value).toLocaleString('sv-SE');
  } catch {
    return value;
  }
};

const IssuesListView: React.FC<IssuesListViewProps> = ({ issues, onEditIssue, onDeleteIssue, canEdit = true, canDelete = true }) => {
  const [sortKey, setSortKey] = useState<'priority' | 'title' | 'type' | 'status' | 'assignee' | 'updatedAt'>('priority');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const sortedIssues = useMemo(() => {
    const data = [...issues];
    data.sort((a, b) => {
      let diff = 0;
      switch (sortKey) {
        case 'priority':
          diff = a.priority - b.priority;
          break;
        case 'title':
          diff = a.title.localeCompare(b.title);
          break;
        case 'type':
          diff = (a.type || '').localeCompare(b.type || '');
          break;
        case 'status':
          diff = (statusOrder[a.status] || 99) - (statusOrder[b.status] || 99);
          break;
        case 'assignee': {
          const aName = a.assignee ? (a.assignee.firstName || a.assignee.email || '') : '';
          const bName = b.assignee ? (b.assignee.firstName || b.assignee.email || '') : '';
          diff = aName.localeCompare(bName);
          break;
        }
        case 'updatedAt':
          diff = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
          break;
        default:
          diff = 0;
      }
      return sortDir === 'asc' ? diff : -diff;
    });
    return data;
  }, [issues, sortKey, sortDir]);

  const handleSort = (key: typeof sortKey) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  if (!issues.length) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: 'grey.50' }}>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
          Inga ärenden ännu
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Klicka på "Nytt ärende" ovan för att skapa ditt första ärende.
        </Typography>
      </Paper>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: 'grey.50' }}>
            <TableCell sortDirection={sortKey === 'priority' ? sortDir : false} sx={{ fontWeight: 600, width: 100 }}>
              <TableSortLabel active={sortKey === 'priority'} direction={sortKey === 'priority' ? sortDir : 'asc'} onClick={() => handleSort('priority')}>
                Prioritet
              </TableSortLabel>
            </TableCell>
            <TableCell sortDirection={sortKey === 'title' ? sortDir : false} sx={{ fontWeight: 600 }}>
              <TableSortLabel active={sortKey === 'title'} direction={sortKey === 'title' ? sortDir : 'asc'} onClick={() => handleSort('title')}>
                Ärende
              </TableSortLabel>
            </TableCell>
            <TableCell sortDirection={sortKey === 'type' ? sortDir : false} sx={{ fontWeight: 600, width: 120 }}>
              <TableSortLabel active={sortKey === 'type'} direction={sortKey === 'type' ? sortDir : 'asc'} onClick={() => handleSort('type')}>
                Typ
              </TableSortLabel>
            </TableCell>
            <TableCell sortDirection={sortKey === 'status' ? sortDir : false} sx={{ fontWeight: 600, width: 130 }}>
              <TableSortLabel active={sortKey === 'status'} direction={sortKey === 'status' ? sortDir : 'asc'} onClick={() => handleSort('status')}>
                Status
              </TableSortLabel>
            </TableCell>
            <TableCell sortDirection={sortKey === 'assignee' ? sortDir : false} sx={{ fontWeight: 600, width: 120 }}>
              <TableSortLabel active={sortKey === 'assignee'} direction={sortKey === 'assignee' ? sortDir : 'asc'} onClick={() => handleSort('assignee')}>
                Ansvarig
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ fontWeight: 600, width: 80 }}>Bilagor</TableCell>
            <TableCell sortDirection={sortKey === 'updatedAt' ? sortDir : false} sx={{ fontWeight: 600, width: 160 }}>
              <TableSortLabel active={sortKey === 'updatedAt'} direction={sortKey === 'updatedAt' ? sortDir : 'asc'} onClick={() => handleSort('updatedAt')}>
                Senast ändrad
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, width: 100 }}>Åtgärder</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedIssues.map((issue) => (
            <TableRow 
              key={issue._id} 
              hover 
              sx={{ 
                '&:hover': { backgroundColor: 'action.hover' },
                borderLeft: `4px solid`,
                borderLeftColor: issue.priority === 1 ? 'error.main' : issue.priority === 2 ? 'warning.main' : 'transparent',
              }}
            >
              <TableCell>
                <Tooltip title={priorityLabel(issue.priority)}>
                  <Chip 
                    size="small" 
                    label={`P${issue.priority}`} 
                    color={priorityColor(issue.priority)} 
                    sx={{ minWidth: 40 }}
                  />
                </Tooltip>
              </TableCell>
              <TableCell sx={{ maxWidth: 400 }}>
                <Typography variant="body2" fontWeight={600}>
                  {issue.title}
                </Typography>
                {issue.description && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {issue.description.length > 100 ? issue.description.substring(0, 100) + '...' : issue.description}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Chip 
                  size="small" 
                  label={typeLabel[issue.type] || issue.type} 
                  color={typeColor(issue.type)}
                  variant="outlined"
                />
              </TableCell>
              <TableCell>
                <Chip 
                  size="small" 
                  label={statusLabel[issue.status] || issue.status} 
                  color={statusColor(issue.status)}
                  variant={issue.status === 'done' ? 'filled' : 'outlined'}
                />
              </TableCell>
              <TableCell>
                {issue.assignee ? (
                  <Typography variant="body2">
                    {issue.assignee.firstName || issue.assignee.email}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">—</Typography>
                )}
              </TableCell>
              <TableCell>
                {issue.attachments?.length ? (
                  <Chip size="small" label={`📎 ${issue.attachments.length}`} variant="outlined" sx={{ height: 22 }} />
                ) : (
                  <Typography variant="body2" color="text.secondary">—</Typography>
                )}
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {formatDate(issue.updatedAt)}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                  {canEdit && (
                    <Tooltip title="Redigera">
                      <IconButton size="small" onClick={() => onEditIssue(issue)} color="primary">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canDelete && (
                    <Tooltip title="Ta bort">
                      <IconButton size="small" color="error" onClick={() => onDeleteIssue(issue._id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default IssuesListView;
